import * as z from "zod";
import { Id, IsoDate } from "./ids.js";

/**
 * Preá Kids: child profiles inside a family (the parent's workspace). Wire names are part of
 * the contract with the UI; change them only together with docs/kids/design.md.
 */

export const AGE_BANDS = ["6-9", "10-12", "13-15", "16-17"] as const;
export const AgeBandSchema = z.enum(AGE_BANDS);
export type AgeBand = z.infer<typeof AgeBandSchema>;

export const CHILD_MIN_AGE = 6;
export const CHILD_MAX_AGE = 17;

/**
 * The band for a birth year in `year`. Only the year is stored, so the age used is the lower
 * bound (one year less than the calendar difference): a child is never placed in an older band
 * than the year allows. Null when the age falls outside 6–17.
 */
export function ageBandFor(birthYear: number, year: number): AgeBand | null {
  const age = year - birthYear - 1;
  if (age < CHILD_MIN_AGE || age > CHILD_MAX_AGE) return null;
  if (age <= 9) return "6-9";
  if (age <= 12) return "10-12";
  if (age <= 15) return "13-15";
  return "16-17";
}

export const CHILD_NAME_MAX = 24;
export const CHILD_AVATAR_IDS = [
  "onca",
  "coruja",
  "tucano",
  "tartaruga",
  "golfinho",
  "raposa",
  "pinguim",
  "coelho",
  "foca",
  "lobo",
  "sapo",
  "abelha",
] as const;
export const ChildAvatarIdSchema = z.enum(CHILD_AVATAR_IDS);

export const ChildProfileSchema = z.object({
  id: Id,
  name: z.string().trim().min(1).max(CHILD_NAME_MAX),
  ageBand: AgeBandSchema,
  avatarId: ChildAvatarIdSchema,
  colorKey: z.string().min(1),
  createdAt: IsoDate,
  archivedAt: IsoDate.nullable(),
});
export type ChildProfile = z.infer<typeof ChildProfileSchema>;

/** Features a child may use. Purchases, external messaging and integrations are never offered. */
export const ChildFeaturesSchema = z.object({
  computer: z.boolean(),
  web: z.boolean(),
  routines: z.boolean(),
  voice: z.boolean(),
  homeworkPhoto: z.boolean(),
});
export type ChildFeatures = z.infer<typeof ChildFeaturesSchema>;

export const HH_MM = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const CHILD_CONTENT_LEVELS = ["kids", "teen"] as const;
export const ChildContentLevelSchema = z.enum(CHILD_CONTENT_LEVELS);
export type ChildContentLevel = z.infer<typeof ChildContentLevelSchema>;

export const CHILD_VISIBILITY = ["full", "summary"] as const;
export const ChildVisibilitySchema = z.enum(CHILD_VISIBILITY);
export type ChildVisibility = z.infer<typeof ChildVisibilitySchema>;

export const ChildControlsSchema = z.object({
  profileId: Id,
  /** Bumped on every change; a stale write is refused. */
  version: z.number().int().nonnegative(),
  features: ChildFeaturesSchema,
  dailyLimitMinutes: z.number().int().min(15).max(480),
  quietHours: z.object({ start: HH_MM, end: HH_MM }),
  visibility: ChildVisibilitySchema,
  contentLevel: ChildContentLevelSchema,
  directAnswerSubjects: z.array(z.string().min(1).max(40)).max(12),
  allowedSkillIds: z.array(Id).max(20),
});
export type ChildControls = z.infer<typeof ChildControlsSchema>;

export const ChildControlsPatchSchema = ChildControlsSchema.omit({
  profileId: true,
  version: true,
  visibility: true,
})
  .extend({ features: ChildFeaturesSchema.partial() })
  .partial()
  .strict();
export type ChildControlsPatch = z.infer<typeof ChildControlsPatchSchema>;

export const TUTOR_SUBJECTS = [
  "matematica",
  "portugues",
  "ciencias",
  "historia",
  "geografia",
  "ingles",
  "outras",
] as const;
export const TutorSubjectSchema = z.enum(TUTOR_SUBJECTS);
export type TutorSubject = z.infer<typeof TutorSubjectSchema>;

/** Metadata on a child's tutor reply. The client renders the stage; the server decides it. */
export const TutorBlockSchema = z.object({
  kind: z.literal("tutor"),
  stage: z.enum(["hint", "check", "answer"]),
  hintIndex: z.number().int().nonnegative().nullable(),
  hintTotal: z.number().int().positive().nullable(),
  subject: TutorSubjectSchema.nullable(),
  checkResult: z.enum(["correct", "partly", "incorrect"]).nullable(),
});
export type TutorBlock = z.infer<typeof TutorBlockSchema>;

export const KIDS_PLAN_LIMIT_CODE = "plan_child_limit";
export const KIDS_PIN_LOCKED_CODE = "pin_locked";
export const KIDS_CONFLICT_CODE = "controls_conflict";

export const KidsPlanSummarySchema = z.object({
  planId: z.string(),
  planName: z.string(),
  /** Null means the plan has no limit (Enterprise). */
  childProfilesLimit: z.number().int().nonnegative().nullable(),
  childProfilesUsed: z.number().int().nonnegative(),
  canCreate: z.boolean(),
  upgradeTo: z.enum(["plus"]).nullable(),
});
export type KidsPlanSummary = z.infer<typeof KidsPlanSummarySchema>;

/** The visibility a parent gets for a band: full text for 6–12, summaries and safety alerts for 13–17. */
export function visibilityForBand(band: AgeBand): ChildVisibility {
  return band === "6-9" || band === "10-12" ? "full" : "summary";
}

/** The categories the safety classifier may return for a child's text. */
export const SAFETY_CATEGORIES = [
  "sexual",
  "violence",
  "self_harm",
  "abuse",
  "drugs",
  "hate",
  "stranger_contact",
  "personal_data",
  "jailbreak",
  "grooming",
  "other",
] as const;
export type SafetyCategory = (typeof SAFETY_CATEGORIES)[number];

/** The classifier's answer for one piece of child text. Anything else is refused as unjudged. */
export const SafetyVerdictSchema = z.object({
  action: z.enum(["allow", "refuse", "crisis"]),
  categories: z.array(z.enum(SAFETY_CATEGORIES)).max(SAFETY_CATEGORIES.length),
});

// RPC contracts. The UI and the API both import these; names are stable for the launch build.

export const PinSchema = z.string().regex(/^\d{6}$/);
export const PairingCodeSchema = z.string().regex(/^\d{6}$/);
/** Header carrying a child session token. Parent routes never read it. */
export const CHILD_TOKEN_HEADER = "x-prea-child-token";

const ProfileBirthYearSchema = z.number().int().min(1900).max(2100);

export const PARENT_AGE_METHODS = ["attestation_paid_plan", "cpf_birth_date"] as const;
export type ParentAgeMethod = (typeof PARENT_AGE_METHODS)[number];

export const KidsSummarySchema = z.object({
  kidsEnabled: z.boolean(),
  plan: KidsPlanSummarySchema,
  profiles: z.array(ChildProfileSchema),
  pinSet: z.boolean(),
  pinLockedUntil: IsoDate.nullable(),
  timezoneSet: z.boolean(),
  /** How the parent's adult status was established; null means child profiles are not open yet. */
  parentAgeMethod: z.enum(PARENT_AGE_METHODS).nullable(),
});
export type KidsSummary = z.infer<typeof KidsSummarySchema>;

/** The guardian consent text version. A client must send the current one; a stale version is refused. */
export const KIDS_CONSENT_VERSION = "kids-consent-v1";
export const GuardianConsentInputSchema = z.object({
  version: z.string(),
  /** The parent confirms being an adult, the account holder. Sets the parent age evidence. */
  attestedAdult: z.literal(true),
  /** Consent to send this child's chats to DeepSeek directly. Off means orbin only. */
  deepseekDirect: z.boolean(),
});

export const ProfileCreateInputSchema = z.object({
  name: z.string().trim().min(1).max(CHILD_NAME_MAX),
  birthYear: ProfileBirthYearSchema,
  avatarId: ChildAvatarIdSchema,
  colorKey: z.string().min(1),
  /** Required only while the family has no PIN; the first child profile sets it. */
  pin: PinSchema.optional(),
  guardianConsent: GuardianConsentInputSchema,
});
export type ProfileCreateInput = z.infer<typeof ProfileCreateInputSchema>;

export const ProfileUpdateInputSchema = z.object({
  profileId: Id,
  name: z.string().trim().min(1).max(CHILD_NAME_MAX).optional(),
  birthYear: ProfileBirthYearSchema.optional(),
  avatarId: ChildAvatarIdSchema.optional(),
  colorKey: z.string().min(1).optional(),
});
export const ProfileArchiveInputSchema = z.object({ profileId: Id });
/** Permanent delete. The name must be typed again as confirmation. */
export const ProfileDeleteInputSchema = z.object({
  profileId: Id,
  confirmName: z.string().trim().min(1).max(CHILD_NAME_MAX),
});

export const PinSetInputSchema = z.object({ pin: PinSchema });
export const PinVerifyInputSchema = z.object({ pin: PinSchema });
export const PinResetInputSchema = z.object({
  /** The parent's account password, re-checked on the server. */
  password: z.string().min(1).max(256),
  pin: PinSchema,
});
/** A wrong PIN is an answer, not an error. Lockout is the pin_locked error code. */
export const KidsPinResultSchema = z.discriminatedUnion("ok", [
  z.object({ ok: z.literal(true) }),
  z.object({
    ok: z.literal(false),
    attemptsLeft: z.number().int().nonnegative(),
    lockedUntil: IsoDate.nullable(),
  }),
]);
export type KidsPinResult = z.infer<typeof KidsPinResultSchema>;

export const PairingCreateInputSchema = z.object({ profileId: Id });
export const PairingCodeResultSchema = z.object({
  code: PairingCodeSchema,
  expiresAt: IsoDate,
});
/** Called from the child's device without a session; rate limited by the server. */
export const PairingRedeemInputSchema = z.object({ code: PairingCodeSchema });

export const SessionStartInputSchema = z.object({ profileId: Id, pin: PinSchema });
export const ChildSessionSchema = z.object({
  token: z.string().min(32),
  profileId: Id,
  botId: Id,
  expiresAt: IsoDate,
});
export type ChildSession = z.infer<typeof ChildSessionSchema>;
/** Child device leaves the child session. The PIN returns the parent. */
export const SessionExitInputSchema = z.object({ pin: PinSchema });

export const ChildMeSchema = z.object({
  profile: ChildProfileSchema,
  botId: Id,
  features: ChildFeaturesSchema,
  dailyLimitMinutes: z.number().int().nonnegative(),
  usedTodayMinutes: z.number().int().nonnegative(),
  quietNow: z.boolean(),
  visibility: ChildVisibilitySchema,
});
export type ChildMe = z.infer<typeof ChildMeSchema>;

export const ActivityListInputSchema = z.object({
  profileId: Id,
  days: z.number().int().min(1).max(30).default(7),
});
export const ActivityItemSchema = z.object({
  id: Id,
  at: IsoDate,
  kind: z.enum(["chat", "session", "blocked"]),
  subject: TutorSubjectSchema.nullable(),
  minutes: z.number().int().nonnegative().nullable(),
  /** The child's text; set only when the profile visibility is full. */
  text: z.string().nullable(),
  /** A thread summary; set only when the profile visibility is summary. */
  summary: z.string().nullable(),
});
export const ActivityListSchema = z.object({
  visibility: ChildVisibilitySchema,
  items: z.array(ActivityItemSchema),
});

export const ALERT_KINDS = [
  "safety",
  "crisis",
  "time_limit",
  "quiet_hours",
  "feature_blocked",
  "unjudged",
] as const;
export const AlertKindSchema = z.enum(ALERT_KINDS);
export type AlertKind = z.infer<typeof AlertKindSchema>;
export const AlertSchema = z.object({
  id: Id,
  profileId: Id,
  at: IsoDate,
  kind: AlertKindSchema,
  severity: z.enum(["info", "warning", "critical"]),
  category: z.enum(SAFETY_CATEGORIES).nullable(),
  acknowledgedAt: IsoDate.nullable(),
});
export type Alert = z.infer<typeof AlertSchema>;
export const AlertsListInputSchema = z.object({
  profileId: Id.optional(),
  unackedOnly: z.boolean().default(false),
});
export const AlertAcknowledgeInputSchema = z.object({ alertId: Id });
/** A parent deletes one alert they have already marked as read; an unread alert is refused. */
export const AlertDeleteInputSchema = z.object({ alertId: Id });
export const AlertsListSchema = z.object({ items: z.array(AlertSchema) });

export const ReportSummarySchema = z.object({
  id: Id,
  profileId: Id,
  periodStart: IsoDate,
  periodEnd: IsoDate,
  totalMinutes: z.number().int().nonnegative(),
  days: z.array(z.object({ date: z.string(), minutes: z.number().int().nonnegative() })),
  subjects: z.array(
    z.object({ subject: TutorSubjectSchema, count: z.number().int().nonnegative() }),
  ),
  safetyAlertCount: z.number().int().nonnegative(),
  /** Thread summaries for summary-visibility profiles; null for full visibility. */
  summaries: z.array(z.string()).nullable(),
});
export type ReportSummary = z.infer<typeof ReportSummarySchema>;
export const ReportListInputSchema = z.object({ profileId: Id });
export const ReportListSchema = z.object({ items: z.array(ReportSummarySchema) });
export const ReportGetInputSchema = z.object({ reportId: Id });
export const ReportDeleteInputSchema = z.object({ reportId: Id });
/** Deletes the child's whole conversation with her Preá. The profile, its reports and its alerts stay. */
export const ActivityClearInputSchema = z.object({ profileId: Id });

export const ControlsGetInputSchema = z.object({ profileId: Id });
export const ControlsUpdateInputSchema = z.object({
  profileId: Id,
  /** The version the parent read; a stale write fails with controls_conflict. */
  expectedVersion: z.number().int().nonnegative(),
  patch: ChildControlsPatchSchema,
});

/** Error payload for a plan that has no child profile left. */
export const PlanChildLimitPayloadSchema = z.object({
  code: z.literal(KIDS_PLAN_LIMIT_CODE),
  planId: z.string(),
  limit: z.number().int().nonnegative(),
  used: z.number().int().nonnegative(),
  upgradeTo: z.enum(["plus"]).nullable(),
});

/** Result of a homework photo. A photo is kept only after the judge allows it and the photo switch is on. */
export const HomeworkResultSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("accepted"), photoId: Id }),
  z.object({ status: z.literal("rejected_safety") }),
  z.object({ status: z.literal("disabled") }),
]);
export type HomeworkResult = z.infer<typeof HomeworkResultSchema>;

/** A kept homework photo, as the parent sees it. The photo itself is not shown in the dashboard. */
export const HomeworkPhotoSchema = z.object({
  id: Id,
  profileId: Id,
  mimeType: z.enum(["image/jpeg", "image/png", "image/webp"]),
  byteSize: z.number().int().nonnegative(),
  createdAt: IsoDate,
});
export type HomeworkPhoto = z.infer<typeof HomeworkPhotoSchema>;
export const HomeworkPhotoListInputSchema = z.object({ profileId: Id });
export const HomeworkPhotoListSchema = z.object({ items: z.array(HomeworkPhotoSchema) });
export const HomeworkPhotoDeleteInputSchema = z.object({ photoId: Id });

/** The largest photo the child may send, as base64 text (about 4.5 MB of image). */
export const HOMEWORK_PHOTO_MAX_BASE64 = 6_000_000;

/** A homework photo from the child's device. The bytes are judged before anything keeps or sends them. */
export const HomeworkUploadInputSchema = z.object({
  mimeType: z.enum(["image/jpeg", "image/png", "image/webp"]),
  imageBase64: z.string().min(1).max(HOMEWORK_PHOTO_MAX_BASE64),
});
export type HomeworkUploadInput = z.infer<typeof HomeworkUploadInputSchema>;

/**
 * The report channel (art. 27–28). A child or parent reports content, a bot reply or a problem. The
 * note is free text and stays in the review queue; the parent sees the status only.
 */
export const REPORT_CATEGORIES = ["inappropriate_content", "security_problem", "other"] as const;
export const ReportCategorySchema = z.enum(REPORT_CATEGORIES);
export type ReportCategory = z.infer<typeof ReportCategorySchema>;
export const REPORT_NOTE_MAX = 500;
export const ReportChannelCreateInputSchema = z.object({
  /** The reported message in the child's Preá, when the report is about one. */
  messageId: Id.optional(),
  category: ReportCategorySchema,
  note: z.string().trim().max(REPORT_NOTE_MAX).optional(),
});
export const ReportChannelParentCreateInputSchema = ReportChannelCreateInputSchema.extend({ profileId: Id });
export const ReportChannelListInputSchema = z.object({ profileId: Id });
export const ReportChannelResolveInputSchema = z.object({ reportId: Id });
/** What the parent sees of a report: its status, never the note. */
export const ReportChannelItemSchema = z.object({
  id: Id,
  profileId: Id,
  reporterRole: z.enum(["child", "parent"]),
  category: ReportCategorySchema,
  status: z.enum(["open", "resolved"]),
  createdAt: IsoDate,
  resolvedAt: IsoDate.nullable(),
});
export const ReportChannelListSchema = z.object({ items: z.array(ReportChannelItemSchema) });
export const ReportChannelReceiptSchema = z.object({ id: Id, status: z.literal("open"), acknowledgement: z.string() });

/** The refusal codes a child turn can end with. A contest names one of them. */
export const CHILD_REFUSAL_CODES = [
  "route_not_allowed",
  "feature_off",
  "timezone_unset",
  "quiet_hours",
  "daily_limit",
  "credit_cap",
  "provider_not_allowed",
  "image_not_moderated",
  "usage_unavailable",
  "classifier_unavailable",
  "input_refused",
  "profile_unavailable",
  "gate_unavailable",
] as const;
export const ChildRefusalCodeSchema = z.enum(CHILD_REFUSAL_CODES);
/** A contest of a blocked turn (art. 27 of Decreto 12.880). It queues a review; it never re-runs the model or the judge. */
export const ContestInputSchema = z.object({ messageId: Id, refusalCode: ChildRefusalCodeSchema });
export const ContestParentInputSchema = ContestInputSchema.extend({ profileId: Id });
export const ContestReceiptSchema = z.object({ id: Id, status: z.literal("open"), acknowledgement: z.string() });
